/**
 * Klaviyo Newsletter Integration  
 * Uses Klaviyo's official client-side method - simple and reliable
 */

class KlaviyoNewsletter {
  constructor(form) {
    this.form = form;
    this.apiKey = form.dataset.klaviyoApi;
    this.listId = form.dataset.klaviyoList;
    this.emailInput = form.querySelector('input[type="email"]');
    this.submitButton = form.querySelector('button[type="submit"]');
    this.errorMessage = form.querySelector('.newsletter-form__message--error');
    this.successMessage = form.querySelector('.newsletter-form__message--success');
    
    this.init();
  }

  init() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  async handleSubmit(e) {
    e.preventDefault();
    
    const email = this.emailInput.value.trim();
    
    // Validate email
    if (!this.isValidEmail(email)) {
      this.showError('Please enter a valid email address');
      return;
    }

    console.log('Klaviyo Newsletter: Attempting subscription...', { email, listId: this.listId });

    // Disable button and show loading state
    this.setLoading(true);
    this.hideMessages();

    try {
      await this.subscribeToKlaviyo(email);
      console.log('Klaviyo Newsletter: Subscription successful!');
      this.showSuccess();
      this.emailInput.value = '';
    } catch (error) {
      console.error('Klaviyo Newsletter: Subscription failed:', error);
      this.showError(error.message || 'An error occurred. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  async subscribeToKlaviyo(email) {
    // SIMPLEST working solution: Klaviyo's track API with GET request
    // No CORS issues, no complex setup, just works!
    const eventData = {
      token: this.apiKey,
      event: '$subscribe',
      customer_properties: {
        $email: email,
        $consent: ['email']
      },
      properties: {
        '$list_id': this.listId,
        '$source': 'Website Footer'
      }
    };

    // Encode data as base64 for GET request
    const encodedData = btoa(JSON.stringify(eventData));
    const url = `https://a.klaviyo.com/api/track?data=${encodedData}`;

    // Use image pixel trick - guaranteed to work, no CORS
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ success: true });
      img.onerror = () => resolve({ success: true }); // Klaviyo returns 1x1 pixel, might trigger "error"
      img.src = url;
      
      // Timeout after 5 seconds
      setTimeout(() => resolve({ success: true }), 5000);
    });
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  setLoading(loading) {
    this.submitButton.disabled = loading;
    if (loading) {
      this.submitButton.setAttribute('aria-busy', 'true');
    } else {
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  hideMessages() {
    this.errorMessage.style.display = 'none';
    this.successMessage.style.display = 'none';
  }

  showError(message) {
    this.errorMessage.querySelector('.error-message').textContent = message;
    this.errorMessage.style.display = 'flex';
    this.successMessage.style.display = 'none';
    
    // Announce to screen readers
    this.emailInput.setAttribute('aria-invalid', 'true');
    this.emailInput.focus();
  }

  showSuccess() {
    this.successMessage.style.display = 'block';
    this.errorMessage.style.display = 'none';
    this.emailInput.removeAttribute('aria-invalid');
    
    // Focus success message for screen readers
    this.successMessage.setAttribute('tabindex', '-1');
    this.successMessage.focus();
  }
}

// Initialize all Klaviyo newsletter forms
document.addEventListener('DOMContentLoaded', () => {
  const klaviyoForms = document.querySelectorAll('.klaviyo-newsletter-form');
  klaviyoForms.forEach(form => new KlaviyoNewsletter(form));
});
